import { EncodedAudioPacketSource, EncodedPacket, EncodedVideoPacketSource, Output, WebMOutputFormat } from 'mediabunny';
import { FRAME_RATE, SMOKE_FRAME_COUNT, SMOKE_SAMPLE_COUNT, frameDurationUs, frameTimestampUs } from '../core/time';
import { collectCapabilities, probeCodec } from './capabilities';
import type { CodecConfigRecord, SmokeProgress, SmokeReport, SmokeResult, StructuredError } from './types';
import { OpfsWorkspace, WorkspaceError } from './workspace';

const WIDTH = 480;
const HEIGHT = 480;
// Full VP9 codec string: 4:2:0 + BT.709 primaries, transfer and matrix, limited range.
// Chromium otherwise defaults the encoded stream to legacy SD colour metadata.
const VIDEO_CODEC = 'vp09.00.10.08.01.01.01.01.00';
const HARDWARE_ACCELERATION = ['prefer-hardware', 'no-preference'] as const;
const VIDEO_COLOR_SPACE = {
  primaries: 'bt709',
  transfer: 'bt709',
  matrix: 'bt709',
  fullRange: false
} as const;
const AUDIO_CONFIG = {
  codec: 'opus',
  sampleRate: 48_000,
  numberOfChannels: 2,
  bitrate: 128_000
} as const;

type EncoderConfig = Record<string, unknown>;
export type CancellationCheckpoint = 'CANCEL_RENDER' | 'CANCEL_MUX' | 'CANCEL_PRE_READY';

class SmokeCancelled extends Error {
  constructor(readonly checkpoint: CancellationCheckpoint | 'USER_CANCEL') {
    super(`Export cancelled at ${checkpoint}.`);
    this.name = 'SmokeCancelled';
  }
}

function structuredError(code: string, stage: string, error: unknown, recoverable = true): StructuredError {
  return {
    code,
    stage,
    recoverable,
    message: error instanceof Error ? error.message : String(error)
  };
}

function createRunId(): string {
  return `s0-webm-opus-${new Date().toISOString().replaceAll(/[:.]/g, '-')}-${crypto.randomUUID()}`;
}

function createCanvas(): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = WIDTH;
  canvas.height = HEIGHT;
  return canvas;
}

function drawFrame(canvas: HTMLCanvasElement, index: number): void {
  const context = canvas.getContext('2d', { alpha: false });
  if (!context) {
    throw new Error('Canvas 2D is unavailable.');
  }
  const hue = (index * 17) % 360;
  context.fillStyle = `hsl(${hue} 72% 24%)`;
  context.fillRect(0, 0, WIDTH, HEIGHT);
  context.fillStyle = index % 24 === 0 ? '#ffffff' : '#111111';
  context.fillRect(0, 0, WIDTH, 24);
  context.fillStyle = '#ffffff';
  context.font = 'bold 96px sans-serif';
  context.textAlign = 'center';
  context.fillText(String(index).padStart(3, '0'), WIDTH / 2, HEIGHT / 2);
  context.fillStyle = '#2de2a6';
  context.fillRect(index * 4, HEIGHT - 24, 4, 24);
}

function createPcm(): Float32Array {
  const pcm = new Float32Array(2 * SMOKE_SAMPLE_COUNT);
  for (let channel = 0; channel < 2; channel += 1) {
    const channelOffset = channel * SMOKE_SAMPLE_COUNT;
    for (let sampleIndex = 0; sampleIndex < SMOKE_SAMPLE_COUNT; sampleIndex += 1) {
      const sine = Math.sin((sampleIndex / 48_000) * Math.PI * 2 * 440) * 0.16;
      const impulse = sampleIndex % 48_000 === 0 ? 0.72 : 0;
      pcm[channelOffset + sampleIndex] = sine + impulse;
    }
  }
  return pcm;
}

async function encodeExactOpus(
  pcm: Float32Array,
  source: EncodedAudioPacketSource,
  onEncoderConfig: (config: EncoderConfig) => void
): Promise<void> {
  const framesPerPacket = 960;
  const audioWrites: Promise<void>[] = [];
  let callbackError: Error | null = null;
  const encoder = new AudioEncoder({
    output: (chunk, metadata) => {
      if (chunk.duration === null) {
        callbackError = new Error('Opus encoder emitted a packet without duration.');
        return;
      }
      const endUs = chunk.timestamp + chunk.duration;
      // Chromium emits one final 20 ms packet at 5.000 s on flush. It is encoder
      // padding, outside the fixed five-second presentation interval, not source PCM.
      if (chunk.timestamp < 0 || endUs > 5_000_000) {
        return;
      }
      const data = new Uint8Array(chunk.byteLength);
      chunk.copyTo(data);
      audioWrites.push(source.add(new EncodedPacket(data, 'key', chunk.timestamp / 1_000_000, chunk.duration / 1_000_000), metadata));
    },
    error: (error) => {
      callbackError = error;
    }
  });

  try {
    const config = { ...AUDIO_CONFIG };
    encoder.configure(config);
    onEncoderConfig(config);

    for (let offset = 0; offset < SMOKE_SAMPLE_COUNT; offset += framesPerPacket) {
      const frames = Math.min(framesPerPacket, SMOKE_SAMPLE_COUNT - offset);
      const data = new Float32Array(frames * 2);
      data.set(pcm.subarray(offset, offset + frames), 0);
      data.set(pcm.subarray(SMOKE_SAMPLE_COUNT + offset, SMOKE_SAMPLE_COUNT + offset + frames), frames);
      const audioData = new AudioData({
        format: 'f32-planar',
        sampleRate: 48_000,
        numberOfFrames: frames,
        numberOfChannels: 2,
        timestamp: Math.round((offset / 48_000) * 1_000_000),
        data
      });
      encoder.encode(audioData);
      audioData.close();
      if (encoder.encodeQueueSize >= 4) {
        await new Promise<void>((resolve) => encoder.addEventListener('dequeue', () => resolve(), { once: true }));
      }
    }
    await encoder.flush();
    if (callbackError) {
      throw callbackError;
    }
    await Promise.all(audioWrites);
  } finally {
    encoder.close();
  }
}

async function encodeExactVp9(
  canvas: HTMLCanvasElement,
  source: EncodedVideoPacketSource,
  hardwareAcceleration: 'prefer-hardware' | 'no-preference',
  onEncoderConfig: (config: EncoderConfig) => void,
  onEncodedFrame: (index: number) => Promise<void>
): Promise<void> {
  const videoWrites: Promise<void>[] = [];
  let callbackError: Error | null = null;
  const encoder = new VideoEncoder({
    output: (chunk, metadata) => {
      if (chunk.duration === null) {
        callbackError = new Error('VP9 encoder emitted a packet without duration.');
        return;
      }
      const data = new Uint8Array(chunk.byteLength);
      chunk.copyTo(data);
      const forcedMetadata = metadata?.decoderConfig
        ? {
            ...metadata,
            decoderConfig: {
              ...metadata.decoderConfig,
              codec: VIDEO_CODEC,
              codedWidth: WIDTH,
              codedHeight: HEIGHT,
              colorSpace: VIDEO_COLOR_SPACE
            }
          }
        : metadata;
      videoWrites.push(source.add(new EncodedPacket(data, chunk.type, chunk.timestamp / 1_000_000, chunk.duration / 1_000_000), forcedMetadata));
    },
    error: (error) => {
      callbackError = error;
    }
  });

  try {
    const config = {
      codec: VIDEO_CODEC,
      width: WIDTH,
      height: HEIGHT,
      bitrate: 2_500_000,
      framerate: FRAME_RATE,
      hardwareAcceleration,
      latencyMode: 'quality' as const
    };
    encoder.configure(config);
    onEncoderConfig(config);
    for (let index = 0; index < SMOKE_FRAME_COUNT; index += 1) {
      drawFrame(canvas, index);
      const frame = new VideoFrame(canvas, {
        timestamp: frameTimestampUs(index),
        duration: frameDurationUs(index)
      });
      encoder.encode(frame, { keyFrame: index === 0 || index % 48 === 0 });
      frame.close();
      if (encoder.encodeQueueSize >= 4) {
        await new Promise<void>((resolve) => encoder.addEventListener('dequeue', () => resolve(), { once: true }));
      }
      await onEncodedFrame(index);
    }
    await encoder.flush();
    if (callbackError) {
      throw callbackError;
    }
    await Promise.all(videoWrites);
  } finally {
    encoder.close();
  }
}

function videoProbeConfigs(): EncoderConfig[] {
  return HARDWARE_ACCELERATION.map((hardwareAcceleration) => ({
    codec: VIDEO_CODEC,
    width: WIDTH,
    height: HEIGHT,
    bitrate: 2_500_000,
    framerate: FRAME_RATE,
    hardwareAcceleration,
    latencyMode: 'quality',
  }));
}

function initialReport(runId: string, video: CodecConfigRecord[], audio: CodecConfigRecord[]): SmokeReport {
  return {
    schemaVersion: 1,
    runId,
    scenario: { id: 'S0-WEBM-OPUS-5S', frameCount: SMOKE_FRAME_COUNT, durationTicks: SMOKE_SAMPLE_COUNT },
    environment: {
      commit: __BITPOP_COMMIT__,
      treeState: __BITPOP_TREE_STATE__,
      userAgent: navigator.userAgent,
      origin: window.location.origin,
      executedAt: new Date().toISOString()
    },
    capabilities: collectCapabilities(),
    codecs: { video, audio, muxer: 'mediabunny@1.52.3' },
    measurements: { totalMs: 0, encodedFrames: 0, pcmFrames: SMOKE_SAMPLE_COUNT, artifactBytes: null },
    artifact: { fileName: null, mimeType: null, sha256: null, exportId: null, ready: false },
    cleanup: { checkpoint: null, orphanCount: null, lockReleased: null },
    status: 'NON_CONFORMANT',
    errors: []
  };
}

export type SmokeRunOptions = {
  onProgress?: (progress: SmokeProgress) => void;
  signal?: AbortSignal;
  cancelAt?: CancellationCheckpoint;
  maxOutputBytesForTest?: number;
};

function reportProgress(options: SmokeRunOptions, progress: SmokeProgress): void {
  options.onProgress?.(progress);
}

function nextPaint(): Promise<void> {
  return new Promise((resolve) => requestAnimationFrame(() => resolve()));
}

function throwIfCancelled(options: SmokeRunOptions): void {
  if (options.signal?.aborted) throw new SmokeCancelled('USER_CANCEL');
}

export async function runSmoke(options: SmokeRunOptions = {}): Promise<SmokeResult> {
  const startedAt = performance.now();
  const runId = createRunId();
  reportProgress(options, { stage: 'capability-probe', message: 'Vérification des encodeurs VP9 et Opus…', completed: null, total: null });
  const video = await Promise.all(videoProbeConfigs().map((config) => probeCodec('VideoEncoder', config)));
  const audio = [await probeCodec('AudioEncoder', { ...AUDIO_CONFIG })];
  const report = initialReport(runId, video, audio);
  const selectedVideo = video.find((entry) => entry.supported === true);

  if (!selectedVideo || audio[0]?.supported !== true) {
    report.errors.push({
      code: 'NATIVE_CODEC_UNAVAILABLE',
      stage: 'preflight',
      recoverable: true,
      message: 'The native VP9 or Opus WebCodecs path is unavailable; do not add a fallback silently.'
    });
    report.measurements.totalMs = performance.now() - startedAt;
    reportProgress(options, { stage: 'failed', message: 'Les encodeurs VP9 ou Opus requis ne sont pas disponibles.', completed: null, total: null });
    return { report, artifact: null };
  }

  let workspace: OpfsWorkspace | null = null;
  let artifact: File | null = null;
  let output: Output | null = null;
  let job: Awaited<ReturnType<OpfsWorkspace['createJob']>> | null = null;
  try {
    workspace = await OpfsWorkspace.open({
      maxWriteBytes: options.maxOutputBytesForTest,
      onFirstWrite: options.cancelAt === 'CANCEL_MUX' ? () => { throw new SmokeCancelled('CANCEL_MUX'); } : undefined,
      onBeforeReady: options.cancelAt === 'CANCEL_PRE_READY' ? () => { throw new SmokeCancelled('CANCEL_PRE_READY'); } : undefined,
    });
    return await workspace.runExclusive(async (lockedWorkspace) => {
      reportProgress(options, { stage: 'initializing', message: 'Préparation des pistes VP9 et Opus…', completed: null, total: null });
      throwIfCancelled(options);
      const canvas = createCanvas();
      const videoConfig = selectedVideo.requested;
      const videoEffective: EncoderConfig[] = [];
      const audioEffective: EncoderConfig[] = [];
      const videoSource = new EncodedVideoPacketSource('vp9');
      const audioSource = new EncodedAudioPacketSource('opus');
      job = await lockedWorkspace.createJob(runId);
      output = new Output({ format: new WebMOutputFormat(), target: job.target });
      output.addVideoTrack(videoSource, { frameRate: FRAME_RATE });
      output.addAudioTrack(audioSource);
      reportProgress(options, { stage: 'starting-muxer', message: 'Démarrage du muxeur WebM…', completed: null, total: null });
      await output.start();
      reportProgress(options, { stage: 'encoding-audio', message: 'Encodage des 240 000 échantillons audio…', completed: 0, total: SMOKE_SAMPLE_COUNT });
      await encodeExactOpus(createPcm(), audioSource, (config) => audioEffective.push(config));
      throwIfCancelled(options);
      reportProgress(options, { stage: 'encoding-video', message: 'Encodage des trames vidéo…', completed: 0, total: SMOKE_FRAME_COUNT });

      await encodeExactVp9(
        canvas,
        videoSource,
        videoConfig.hardwareAcceleration as 'prefer-hardware' | 'no-preference',
        (config) => videoEffective.push(config),
        async (index) => {
          report.measurements.encodedFrames += 1;
          if (index === 35 && options.cancelAt === 'CANCEL_RENDER') throw new SmokeCancelled('CANCEL_RENDER');
          throwIfCancelled(options);
          if (index === 0 || (index + 1) % 6 === 0 || index + 1 === SMOKE_FRAME_COUNT) {
            reportProgress(options, {
              stage: 'encoding-video',
              message: `Encodage des trames vidéo : ${index + 1}/${SMOKE_FRAME_COUNT}`,
              completed: index + 1,
              total: SMOKE_FRAME_COUNT
            });
            await nextPaint();
          }
        }
      );
      throwIfCancelled(options);
      reportProgress(options, { stage: 'finalizing', message: 'Finalisation du conteneur WebM…', completed: null, total: null });
      await output.finalize();
      output = null;
      throwIfCancelled(options);
      reportProgress(options, { stage: 'hashing', message: 'Validation, hash et publication transactionnelle…', completed: null, total: null });
      const readyArtifact = await job.publish(5);
      artifact = readyArtifact.file;
      const mimeType = 'video/webm';
      report.codecs.video[video.indexOf(selectedVideo)]!.effective = videoEffective[0] ?? selectedVideo.effective;
      report.codecs.audio[0]!.effective = audioEffective[0] ?? audio[0]!.effective;
      report.measurements.artifactBytes = artifact.size;
      report.artifact = { fileName: artifact.name, mimeType, sha256: readyArtifact.sha256, exportId: readyArtifact.exportId, ready: true };
      report.capabilities.canShareFiles = typeof navigator.canShare === 'function'
        ? navigator.canShare({ files: [artifact] })
        : null;
      report.status = 'PENDING_VALIDATION';
      report.cleanup = { checkpoint: null, orphanCount: (await lockedWorkspace.inventory()).jobs.length, lockReleased: null };
      await lockedWorkspace.writeReport(runId, report);
      reportProgress(options, { stage: 'completed', message: 'WebM prêt : téléchargez-le puis validez-le avec FFmpeg.', completed: SMOKE_FRAME_COUNT, total: SMOKE_FRAME_COUNT });
      return { report, artifact };
    });
  } catch (error) {
    const cancelled = error instanceof SmokeCancelled || options.signal?.aborted;
    report.cleanup.checkpoint = error instanceof SmokeCancelled ? error.checkpoint : options.signal?.aborted ? 'USER_CANCEL' : null;
    report.errors.push(structuredError(cancelled ? 'EXPORT_CANCELLED' : error instanceof WorkspaceError ? error.code : 'SMOKE_ENCODE_FAILED', cancelled ? 'cancel' : 'encode-or-mux', error));
    report.status = cancelled ? 'CANCELLED' : 'NON_CONFORMANT';
    reportProgress(options, { stage: 'failed', message: cancelled ? 'Export annulé et temporaire nettoyé.' : 'Le spike a échoué ; consultez le rapport JSON.', completed: report.measurements.encodedFrames, total: SMOKE_FRAME_COUNT });
    return { report, artifact: null };
  } finally {
    const outputToCancel = output as Output | null;
    const jobToAbort = job as Awaited<ReturnType<OpfsWorkspace['createJob']>> | null;
    // If the StreamTarget sink itself failed, Output.finalize already attempted its
    // close. Calling cancel would retry the errored stream writer and add a false
    // cleanup failure; job.abort below closes the raw OPFS handle and removes it.
    if (outputToCancel && !jobToAbort?.hasTargetWriteFailure()) {
      await outputToCancel.cancel().catch((error: unknown) => report.errors.push(structuredError('SMOKE_CLEANUP_FAILED', 'cleanup', error)));
    }
    if (jobToAbort && !artifact) await jobToAbort.abort();
    report.measurements.totalMs = performance.now() - startedAt;
    if (workspace) {
      try {
        report.cleanup = { ...report.cleanup, orphanCount: (await workspace.inventory()).jobs.length, lockReleased: true };
        await workspace.writeReport(runId, report);
      } catch (error) {
        report.errors.push(structuredError('WORKSPACE_REPORT_WRITE_FAILED', 'cleanup', error));
      }
    }
  }
}
